import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import Draggable from 'react-draggable';
import Paper, { PaperProps } from '@mui/material/Paper';
import {
  Grid,
  Stack,
  Typography,
  IconButton,
  InputBase,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ChatMessage } from '../components/room/types';
import { Socket } from 'socket.io-client';
import { StyledPaperItem } from '../common/mui-components';

const chatWindowHeight = 290;
const chatWindowWidth = 400;
let chatBoxPosition = { x: 0, y: 0 };

const handleChatDragStop = (event: any, dragElement: any) => {
  chatBoxPosition = { x: dragElement.x, y: dragElement.y };
};

const PaperComponent = (props: PaperProps) => {
  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
      defaultPosition={chatBoxPosition}
      onStop={handleChatDragStop}
    >
      <Paper {...props} sx={{ width: '100%' }} />
    </Draggable>
  );
};

type PropTypes = {
  chat?: ChatMessage[];
  closeChat: () => void;
  gameId?: string;
  openChat: boolean;
  profileId: string;
  socket: Socket;
  socketEmitEvent: 'scoreScreenChat' | 'spectatorChat';
  title?: string;
  username: string;
};

export const DraggableChat = ({
  chat,
  closeChat,
  gameId,
  openChat,
  profileId,
  socket,
  socketEmitEvent,
  title,
  username,
}: PropTypes) => {
  const boxRef = useRef<any>(null);
  const inputBaseRef = useRef<any>(null);

  useEffect(() => {
    const keyDownHandler = (event: any) => {
      // if (event.key === 'Escape') {
      //   closeChat();
      //   return;
      // }
      if (event.key === 'Enter') {
        event.preventDefault();
        if (!inputBaseRef.current?.value) return;
        socket.emit(socketEmitEvent, {
          gameId,
          message: inputBaseRef.current.value,
          profileId,
          username,
        });
        inputBaseRef.current.value = '';
      }
    };
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, []);

  useEffect(() => {
    if (openChat) {
      setTimeout(() => {
        focusInputBase();
        scrollToBottomOfChat();
      }, 0);
    }
  }, [openChat]);

  useEffect(() => {
    scrollToBottomOfChat();
  }, [chat]);

  const focusInputBase = () => {
    if (!inputBaseRef.current) return;
    inputBaseRef.current.focus();
  };

  const scrollToBottomOfChat = () => {
    if (!boxRef.current) return;
    const scrollHeight = boxRef.current.scrollHeight;
    boxRef.current.scrollTop = scrollHeight;
  };

  return (
    <Dialog
      open={openChat}
      onClose={closeChat}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      hideBackdrop
      disableEscapeKeyDown
      sx={{ width: `${chatWindowWidth + 90}px` }}
    >
      <Box padding={1} width="100%">
        <Grid
          className="Chat-Grid-Container"
          container
          height="100%"
          spacing={1}
        >
          <Grid className="Chat-Grid-Item" item xs={12}>
            <Grid className="Chat-Grid-Container-2" container spacing={1}>
              <Grid
                className="Chat-Grid-Item-Header"
                item
                xs={12}
                id="draggable-dialog-title"
                sx={{ cursor: 'move' }}
              >
                <Stack
                  direction="row"
                  height={30}
                  justifyContent="space-between"
                  pl={2}
                  sx={{
                    backgroundColor: 'primary.main',
                    borderRadius: '4px',
                    color: 'primary.contrastText',
                  }}
                >
                  <Typography
                    variant="overline"
                    letterSpacing={1}
                    fontWeight={600}
                    width="50%"
                  >
                    {title ? title : 'CHAT'}
                  </Typography>
                  <Typography
                    variant="overline"
                    sx={{
                      width: '50%',
                      display: 'flex',
                      justifyContent: 'right',
                    }}
                  >
                    (click to drag)
                  </Typography>
                  <IconButton size="small" onClick={closeChat}>
                    <CloseIcon sx={{ color: 'primary.contrastText' }} />
                  </IconButton>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <StyledPaperItem
                  onClick={focusInputBase}
                  sx={{
                    height: `${chatWindowHeight}px`,
                    position: 'relative',
                    backgroundColor: 'rgb(16, 18, 21, 0.95)',
                  }}
                >
                  <Box
                    ref={boxRef}
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      overflow: 'auto',
                      height: `${chatWindowHeight}px`,
                      width: { xs: '100%', sm: `${chatWindowWidth}px` },
                      padding: 1,
                    }}
                  >
                    {chat?.map((msg) => (
                      <Stack direction={'row'} display="flex">
                        <Typography
                          ml={1}
                          color="primary.main"
                          textAlign={'left'}
                        >
                          <Tooltip placement="top" title={msg.username}>
                            <span
                              style={{
                                marginRight: 10,
                                textAlign: 'left',
                                color: 'white',
                                minWidth: '50px',
                                maxWidth: '120px',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'pre',
                              }}
                            >
                              {msg.username}:
                            </span>
                          </Tooltip>
                          {msg.message}
                        </Typography>
                      </Stack>
                    ))}
                  </Box>
                </StyledPaperItem>
              </Grid>
              <Grid item xs={12}>
                <StyledPaperItem>
                  <InputBase
                    inputRef={inputBaseRef}
                    sx={{
                      ml: 1,
                      '.css-156xqnd-MuiInputBase-input': { padding: 0 },
                    }}
                    fullWidth
                    placeholder="Type something..."
                  />
                </StyledPaperItem>
              </Grid>
            </Grid>
          </Grid>
          {/* <Grid item xs={4}>
          <Grid item xs={12}>
            <StyledPaperItem sx={{ height: '275px' }}>Channel</StyledPaperItem>
          </Grid>
        </Grid> */}
        </Grid>
      </Box>
    </Dialog>
  );
};
